/**
 * "Invia in chat" di un'offerta nel pannello laterale WhatsApp: genera il PDF
 * del preventivo con la stessa strada dell'allegato email (core
 * documents/quote, template adatto all'offerta) e lo manda come documento
 * nella chat aperta, con una frase di accompagnamento modificabile.
 *
 * Numero Ufficiale: fuori dalla finestra di 24 ore niente invio, solo avviso
 * (stessa lettura della finestra del composer, useCloudCsw).
 * Montato solo quando l'utente apre il riquadro: niente letture a vuoto.
 */
import { useState } from 'react';
import { Loader2, Send, X } from 'lucide-react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useCloudCsw } from '@/lib/hooks/whatsapp/useCloudCsw';
import { useTemplatesForDeal } from '@/lib/hooks/useTemplatesForDeal';
import { generateQuoteDocument, buildClientFileName } from '@/lib/pdf';
import { useApp } from '@/components/AppProvider';
import { tip } from '@/components/messages/ChatTooltip';
import { dealPdfCaption, pdfSendBlock, PDF_SEND_BLOCK_TEXT } from '@/lib/utils/dealWhatsApp';
import type { Deal } from '@/lib/types';

interface Props {
  deal: Deal;
  /** Chat del pannello: l'invio parte solo se e' quella aperta nel composer. */
  chatId: string;
  onClose: () => void;
}

export function DealPdfChatSend({ deal, chatId, onClose }: Props) {
  const { toast } = useApp();
  const selectedChatId = useWhatsAppChatStore((s) => s.selectedChatId);
  const sendMediaMessage = useWhatsAppChatStore((s) => s.sendMediaMessage);
  const { isCloud, cswUnknown, canSendFreeForm } = useCloudCsw(chatId);
  const templates = useTemplatesForDeal(deal.id);
  const templateId = templates.preferred?.id ?? null;
  const [caption, setCaption] = useState(() => dealPdfCaption(deal));
  const [working, setWorking] = useState(false);

  const block = templates.loading
    ? null
    : pdfSendBlock({ chatId, selectedChatId, isCloud, cswUnknown, canSendFreeForm, templateId, caption });
  const disabled = working || templates.loading || block !== null;

  const handleSend = async () => {
    if (disabled || !templateId) return;
    setWorking(true);
    try {
      const blob = await generateQuoteDocument(deal.id, templateId, 'pdf');
      // Ricontrollo dopo la generazione: nel frattempo si puo' aver cambiato chat.
      if (useWhatsAppChatStore.getState().selectedChatId !== chatId) {
        toast.error(PDF_SEND_BLOCK_TEXT.other_chat);
        return;
      }
      const file = new File([blob], buildClientFileName(deal, 'pdf'), { type: 'application/pdf' });
      // Gli errori di invio finiscono sul messaggio in chat (non inviato, con "Riprova").
      await sendMediaMessage(file, 'document', caption.trim() || undefined);
      onClose();
    } catch (e) {
      console.error('[DealPdfChatSend] PDF non generato:', e);
      toast.error('Non sono riuscito a generare il PDF del preventivo');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="mx-2 mb-2 p-2 rounded-md border border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-white dark:bg-white/[0.03] space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-medium text-[#37352F] dark:text-white/90">PDF del preventivo in chat</span>
        <button onClick={onClose} className="p-0.5 rounded text-[#6F6E69] hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10" {...tip('Chiudi senza inviare')}>
          <X className="w-3 h-3" />
        </button>
      </div>
      <textarea
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        rows={3}
        className="w-full px-2 py-1 text-[11px] rounded border border-[rgba(55,53,47,0.12)] dark:border-white/10 bg-transparent text-[#37352F] dark:text-white resize-none focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
        {...tip('Frase che accompagna il PDF: puoi modificarla o lasciarla vuota')}
      />
      {templates.preferred && (
        <p className="text-[10px] text-[#6F6E69] truncate">Template: {templates.preferred.name}</p>
      )}
      {block && <p className="text-[10px] text-[#D9730D]">{PDF_SEND_BLOCK_TEXT[block]}</p>}
      <button
        onClick={handleSend}
        disabled={disabled}
        className="w-full flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-medium rounded bg-[#2383E2] text-white hover:bg-[#1a6fc9] disabled:opacity-50"
        {...tip('Genera il PDF del preventivo e lo invia come documento in questa chat')}
      >
        {working || templates.loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
        {working ? 'Preparo il PDF...' : 'Invia PDF'}
      </button>
    </div>
  );
}
